<!--
  @component LiquidationList
  @description Módulo de liquidación de prestaciones.
               Dos tabs: listado con control de facturas + resumen del período.
-->
<template>
  <div class="liq">
    <!-- Sub-tabs -->
    <div class="liq__subtabs">
      <button
        class="liq__subtab"
        :class="{ 'is-active': activeTab === 'list' }"
        @click="activeTab = 'list'"
      >
        Prestaciones
      </button>
      <button
        class="liq__subtab"
        :class="{ 'is-active': activeTab === 'summary' }"
        @click="
          activeTab = 'summary';
          loadSummary();
        "
      >
        Resumen
      </button>
      <button
        class="liq__subtab"
        :class="{ 'is-active': activeTab === 'batches' }"
        @click="activeTab = 'batches'"
      >
        Lotes
      </button>
      <button
        class="liq__subtab"
        :class="{ 'is-active': activeTab === 'inconsistencies' }"
        @click="activeTab = 'inconsistencies'"
      >
        Inconsistencias<span
          v-if="inconsistencyCount > 0"
          class="liq__badge-count"
          >{{ inconsistencyCount }}</span
        >
      </button>
    </div>

    <!-- TAB: LISTADO -->
    <div v-if="activeTab === 'list'">
      <div class="liq__toolbar">
        <el-input
          v-model="searchInput"
          placeholder="Buscar por TRX, afiliado, CUIT, N° factura..."
          prefix-icon="el-icon-search"
          clearable
          size="small"
          style="width: 300px"
          @input="onSearch"
        />
        <el-select
          v-model="filterStatus"
          placeholder="Todos los estados"
          clearable
          size="small"
          style="width: 160px"
          @change="loadItems"
        >
          <el-option label="Pendiente" value="pending" />
          <el-option label="Factura recibida" value="invoice_received" />
          <el-option label="Liquidado" value="liquidated" />
          <el-option label="Inconsistencia" value="inconsistency" />
          <el-option label="Rechazado" value="rejected" />
        </el-select>
        <el-select
          v-model="filterOrigin"
          placeholder="Todos los orígenes"
          clearable
          size="small"
          style="width: 160px"
          @change="loadItems"
        >
          <el-option label="Validación directa" value="validation" />
          <el-option label="Autorización" value="authorization" />
        </el-select>
        <el-select
          v-model="filterMonth"
          placeholder="Mes"
          clearable
          size="small"
          style="width: 120px"
          @change="loadItems"
        >
          <el-option
            v-for="m in months"
            :key="m.value"
            :label="m.label"
            :value="m.value"
          />
        </el-select>
        <el-select
          v-model="filterYear"
          placeholder="Año"
          clearable
          size="small"
          style="width: 90px"
          @change="loadItems"
        >
          <el-option v-for="y in years" :key="y" :label="y" :value="y" />
        </el-select>
      </div>

      <div class="liq__card">
        <el-table
          v-loading="isLoading"
          :data="items"
          size="small"
          border
          style="width: 100%"
        >
          <el-table-column label="TRX" width="115">
            <template slot-scope="{ row }">
              <span class="liq__trx">{{ row.transactionCode || "-" }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Fecha" width="100">
            <template slot-scope="{ row }">
              <span class="liq__date">{{ row.performedAt | fmtDate }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Afiliado" min-width="150">
            <template slot-scope="{ row }">
              <div class="liq__name">
                {{ row.memberSnapshot.lastName }},
                {{ row.memberSnapshot.firstName }}
              </div>
              <div class="liq__sub">
                {{ row.memberSnapshot.planName || "-" }}
              </div>
            </template>
          </el-table-column>

          <el-table-column
            label="Prestación"
            min-width="180"
            show-overflow-tooltip
          >
            <template slot-scope="{ row }">
              <span class="liq__code">{{ row.practiceSnapshot.code }}</span>
              <span class="liq__pname">{{ row.practiceSnapshot.name }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Prestador" min-width="130">
            <template slot-scope="{ row }">
              <span class="liq__prov">{{
                providerName(row.providerSnapshot, row.provider, row)
              }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Origen" width="140">
            <template slot-scope="{ row }">
              <span
                class="liq__badge"
                :class="originClass(row.origin, row.wasAudited)"
              >
                {{ originLabel(row.origin, row.wasAudited) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="Esperado" width="100" align="right">
            <template slot-scope="{ row }">
              <span class="liq__amount">{{
                row.expectedAmount | fmtMoney
              }}</span>
            </template>
          </el-table-column>

          <el-table-column label="Facturado" width="100" align="right">
            <template slot-scope="{ row }">
              <span
                class="liq__amount"
                :class="{
                  'liq__amount--mismatch':
                    row.invoiceReceived && !row.amountsMatch,
                }"
              >
                {{ row.invoicedAmount | fmtMoney }}
              </span>
            </template>
          </el-table-column>

          <el-table-column label="Estado" width="140">
            <template slot-scope="{ row }">
              <span class="liq__badge" :class="statusClass(row.status)">{{
                statusLabel(row.status)
              }}</span>
            </template>
          </el-table-column>

          <el-table-column label="" width="80" align="center">
            <template slot-scope="{ row }">
              <el-button size="mini" type="text" @click="openDetail(row)"
                >Gestionar</el-button
              >
            </template>
          </el-table-column>
        </el-table>

        <div class="liq__pagination">
          <el-pagination
            :current-page="currentPage"
            :page-size="limit"
            :total="total"
            layout="total, prev, pager, next"
            small
            @current-change="onPageChange"
          />
        </div>
      </div>
    </div>

    <!-- TAB: RESUMEN -->
    <div v-if="activeTab === 'summary'">
      <div class="liq__summary-filters">
        <el-select
          v-model="summaryMonth"
          placeholder="Mes"
          size="small"
          style="width: 120px"
          @change="loadSummary"
        >
          <el-option
            v-for="m in months"
            :key="m.value"
            :label="m.label"
            :value="m.value"
          />
        </el-select>
        <el-select
          v-model="summaryYear"
          placeholder="Año"
          size="small"
          style="width: 90px"
          @change="loadSummary"
        >
          <el-option v-for="y in years" :key="y" :label="y" :value="y" />
        </el-select>
      </div>
      <div class="liq__summary-grid" v-loading="summaryLoading">
        <div class="liq__summary-card">
          <div class="liq__summary-value">{{ summary.total || 0 }}</div>
          <div class="liq__summary-label">Prestaciones realizadas</div>
        </div>
        <div class="liq__summary-card liq__summary-card--green">
          <div class="liq__summary-value">{{ summary.liquidated || 0 }}</div>
          <div class="liq__summary-label">Liquidadas</div>
        </div>
        <div class="liq__summary-card liq__summary-card--orange">
          <div class="liq__summary-value">{{ summary.inconsistency || 0 }}</div>
          <div class="liq__summary-label">Con inconsistencia</div>
        </div>
        <div class="liq__summary-card liq__summary-card--gray">
          <div class="liq__summary-value">
            {{ summary.withoutInvoice || 0 }}
          </div>
          <div class="liq__summary-label">Sin factura</div>
        </div>
      </div>
    </div>

    <!-- TAB: LOTES -->
    <liquidation-batch-list v-if="activeTab === 'batches'" />

    <!-- TAB: INCONSISTENCIAS -->
    <liquidation-inconsistencies
      v-if="activeTab === 'inconsistencies'"
      @resolved="loadInconsistencyCount"
    />

    <!-- DIALOG: GESTIÓN DE FACTURA -->
    <el-dialog
      :visible.sync="dialogVisible"
      :title="`Gestionar liquidación — ${selected && selected.transactionCode}`"
      width="620px"
      @close="selected = null"
    >
      <div v-if="selected" class="liq__dialog">
        <!-- Info -->
        <div class="liq__dialog-row">
          <span class="liq__dialog-label">Afiliado</span>
          <span
            >{{ selected.memberSnapshot.lastName }},
            {{ selected.memberSnapshot.firstName }}</span
          >
        </div>
        <div class="liq__dialog-row">
          <span class="liq__dialog-label">Prestación</span>
          <span
            >{{ selected.practiceSnapshot.code }} —
            {{ selected.practiceSnapshot.name }}</span
          >
        </div>
        <div class="liq__dialog-row">
          <span class="liq__dialog-label">Importe esperado</span>
          <span class="liq__amount">{{
            selected.expectedAmount | fmtMoney
          }}</span>
        </div>

        <el-divider />

        <!-- Formulario factura -->
        <div class="liq__dialog-section">Datos de factura</div>
        <el-row :gutter="12">
          <el-col :span="12">
            <div class="liq__field-label">N° Factura</div>
            <el-input v-model="form.invoiceNumber" size="small" />
          </el-col>
          <el-col :span="12">
            <div class="liq__field-label">CUIT</div>
            <el-input v-model="form.cuit" size="small" />
          </el-col>
        </el-row>
        <el-row :gutter="12" style="margin-top: 10px">
          <el-col :span="24">
            <div class="liq__field-label">Razón Social</div>
            <el-input v-model="form.razonSocial" size="small" />
          </el-col>
        </el-row>
        <el-row :gutter="12" style="margin-top: 10px">
          <el-col :span="12">
            <div class="liq__field-label">Neto sin IVA</div>
            <el-input-number
              v-model="form.netAmount"
              :precision="2"
              :min="0"
              size="small"
              style="width: 100%"
            />
          </el-col>
          <el-col :span="12">
            <div class="liq__field-label">Total con IVA</div>
            <el-input-number
              v-model="form.totalAmount"
              :precision="2"
              :min="0"
              size="small"
              style="width: 100%"
            />
          </el-col>
        </el-row>
        <el-row :gutter="12" style="margin-top: 10px">
          <el-col :span="12">
            <div class="liq__field-label">Fecha de factura</div>
            <el-date-picker
              v-model="form.invoiceDate"
              type="date"
              size="small"
              style="width: 100%"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
            />
          </el-col>
        </el-row>

        <!-- Adjunto -->
        <div class="liq__dialog-section" style="margin-top: 16px">Adjunto</div>
        <el-upload
          action="#"
          :auto-upload="false"
          :on-change="onFileChange"
          :file-list="fileList"
          :limit="1"
          accept=".pdf,.jpg,.jpeg,.png"
        >
          <el-button size="small" icon="el-icon-upload2"
            >Seleccionar archivo</el-button
          >
          <span slot="tip" class="liq__upload-tip">PDF, JPG o PNG</span>
        </el-upload>

        <!-- Notas -->
        <div style="margin-top: 12px">
          <div class="liq__field-label">Notas</div>
          <el-input
            v-model="form.notes"
            type="textarea"
            :rows="2"
            size="small"
          />
        </div>
      </div>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false"
          >Cancelar</el-button
        >
        <el-button
          size="small"
          type="danger"
          plain
          :loading="saving"
          @click="rejectItem"
          >Rechazar</el-button
        >
        <el-button
          size="small"
          type="primary"
          :loading="saving"
          @click="saveInvoice"
          >Guardar factura</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import debounce from "debounce";
import liquidationService from "@/services/liquidation";
import liquidationBatchService from "@/services/liquidation-batch";
import LiquidationBatchList from "./LiquidationBatchList";
import LiquidationInconsistencies from "./LiquidationInconsistencies";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/** @component LiquidationList */
export default {
  name: "LiquidationList",
  components: { LiquidationBatchList, LiquidationInconsistencies },

  filters: {
    /**
     * @description Formatea fecha ISO a dd/MM/yy
     * @param {string} v
     * @returns {string}
     */
    fmtDate(v) {
      if (!v) return "-";
      try {
        return format(parseISO(v), "dd/MM/yy", { locale: es });
      } catch {
        return "-";
      }
    },
    /**
     * @description Formatea número como moneda ARS
     * @param {number} v
     * @returns {string}
     */
    fmtMoney(v) {
      if (v === null || v === undefined) return "—";
      return (
        "$ " +
        Number(v)
          .toFixed(0)
          .replace(/\B(?=(\d{3})+(?!\d))/g, ".")
      );
    },
  },

  data() {
    const now = new Date();
    return {
      activeTab: "list",
      /** @type {number} Cantidad de lotes con inconsistencias */
      inconsistencyCount: 0,
      items: [],
      isLoading: false,
      total: 0,
      currentPage: 1,
      limit: 20,
      searchInput: "",
      filterStatus: "",
      filterOrigin: "",
      filterMonth: now.getMonth() + 1,
      filterYear: now.getFullYear(),
      /** @type {Object} Datos del resumen del período */
      summary: {},
      summaryLoading: false,
      summaryMonth: now.getMonth() + 1,
      summaryYear: now.getFullYear(),
      dialogVisible: false,
      /** @type {Object|null} Item seleccionado para gestionar */
      selected: null,
      saving: false,
      fileList: [],
      pendingFile: null,
      /** @type {Object} Formulario de factura */
      form: {
        invoiceNumber: "",
        cuit: "",
        razonSocial: "",
        netAmount: null,
        totalAmount: null,
        invoiceDate: null,
        notes: "",
      },
      months: [
        { value: 1, label: "Enero" },
        { value: 2, label: "Febrero" },
        { value: 3, label: "Marzo" },
        { value: 4, label: "Abril" },
        { value: 5, label: "Mayo" },
        { value: 6, label: "Junio" },
        { value: 7, label: "Julio" },
        { value: 8, label: "Agosto" },
        { value: 9, label: "Septiembre" },
        { value: 10, label: "Octubre" },
        { value: 11, label: "Noviembre" },
        { value: 12, label: "Diciembre" },
      ],
      years: [2024, 2025, 2026, 2027],
    };
  },

  created() {
    this.onSearch = debounce(this.loadItems, 350);
    this.loadItems();
    this.loadInconsistencyCount();
  },

  methods: {
    /** @description Carga items de liquidación con filtros activos */
    async loadItems() {
      this.isLoading = true;
      try {
        const params = {
          offset: (this.currentPage - 1) * this.limit,
          limit: this.limit,
        };
        if (this.searchInput) params.searchInput = this.searchInput;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.filterOrigin) params.origin = this.filterOrigin;
        if (this.filterMonth) params.billingMonth = this.filterMonth;
        if (this.filterYear) params.billingYear = this.filterYear;
        const result = await liquidationService.get(params);
        this.items = result.docs;
        this.total = result.total;
      } catch (e) {
        this.$message.error("Error al cargar liquidaciones");
      } finally {
        this.isLoading = false;
      }
    },

    /** @description Carga resumen del período seleccionado */
    async loadSummary() {
      this.summaryLoading = true;
      try {
        this.summary = await liquidationService.getSummary({
          billingYear: this.summaryYear,
          billingMonth: this.summaryMonth,
        });
      } catch (e) {
        this.$message.error("Error al cargar resumen");
      } finally {
        this.summaryLoading = false;
      }
    },

    /** @param {number} page */
    onPageChange(page) {
      this.currentPage = page;
      this.loadItems();
    },

    /** @description Abre el dialog de gestión de factura */
    openDetail(row) {
      this.selected = row;
      this.fileList = [];
      this.pendingFile = null;
      this.form = {
        invoiceNumber: row.invoiceData?.invoiceNumber || "",
        cuit: row.invoiceData?.cuit || "",
        razonSocial: row.invoiceData?.razonSocial || "",
        netAmount: row.invoiceData?.netAmount || null,
        totalAmount: row.invoiceData?.totalAmount || null,
        invoiceDate: row.invoiceData?.invoiceDate || null,
        notes: row.notes || "",
      };
      this.dialogVisible = true;
    },

    /** @param {Object} file */
    onFileChange(file) {
      this.pendingFile = file.raw;
    },

    /** @description Guarda datos de factura y sube adjunto si hay */
    async saveInvoice() {
      if (!this.form.totalAmount) {
        this.$message.warning("El total con IVA es obligatorio");
        return;
      }
      this.saving = true;
      try {
        await liquidationService.saveInvoice(this.selected._id, {
          invoiceData: {
            invoiceNumber: this.form.invoiceNumber,
            cuit: this.form.cuit,
            razonSocial: this.form.razonSocial,
            netAmount: this.form.netAmount,
            totalAmount: this.form.totalAmount,
            invoiceDate: this.form.invoiceDate,
          },
          notes: this.form.notes,
        });
        if (this.pendingFile) {
          const fd = new FormData();
          fd.append("file", this.pendingFile);
          await liquidationService.uploadInvoiceFile(this.selected._id, fd);
        }
        this.$message.success("Factura guardada");
        this.dialogVisible = false;
        this.loadItems();
      } catch (e) {
        this.$message.error("Error al guardar factura");
      } finally {
        this.saving = false;
      }
    },

    /** @description Rechaza el item de liquidación */
    async rejectItem() {
      this.saving = true;
      try {
        await liquidationService.updateStatus(this.selected._id, {
          status: "rejected",
          notes: this.form.notes,
        });
        this.$message.success("Prestación rechazada");
        this.dialogVisible = false;
        this.loadItems();
      } catch (e) {
        this.$message.error("Error al rechazar");
      } finally {
        this.saving = false;
      }
    },

    /**
     * @param {Object} s - Provider snapshot
     * @returns {string}
     */
    /**
     * @method providerName
     * @description Resuelve el nombre del prestador desde snapshot, populate directo o populate anidado en serviceRecord
     * @param {Object} snapshot - providerSnapshot del LiquidationItem
     * @param {Object} provider - provider populado del LiquidationItem
     * @param {Object} row - row completo para acceder a serviceRecord.provider
     * @returns {string}
     */
    providerName(snapshot, provider, row) {
      if (snapshot?.firstName)
        return `${snapshot.lastName}, ${snapshot.firstName}`;
      if (provider?.firstName)
        return `${provider.lastName}, ${provider.firstName}`;
      const sr = row?.serviceRecord;
      if (sr?.provider?.firstName)
        return `${sr.provider.lastName}, ${sr.provider.firstName}`;
      return "—";
    },

    /**
     * @param {string} origin
     * @param {boolean} wasAudited
     * @returns {string}
     */
    originClass(origin, wasAudited) {
      if (origin === "authorization" && wasAudited)
        return "liq__badge--audited";
      if (origin === "authorization") return "liq__badge--auth";
      return "liq__badge--validation";
    },

    /**
     * @param {string} origin
     * @param {boolean} wasAudited
     * @returns {string}
     */
    originLabel(origin, wasAudited) {
      if (origin === "authorization" && wasAudited) return "Auth. auditada";
      if (origin === "authorization") return "Autorización";
      return "Validación directa";
    },

    /**
     * @param {string} s
     * @returns {string}
     */
    statusClass(s) {
      return (
        {
          pending: "liq__badge--pending",
          invoice_received: "liq__badge--invoice",
          liquidated: "liq__badge--liquidated",
          inconsistency: "liq__badge--inconsistency",
          rejected: "liq__badge--rejected",
        }[s] || ""
      );
    },

    /**
     * @param {string} s
     * @returns {string}
     */
    statusLabel(s) {
      return (
        {
          pending: "Pendiente",
          invoice_received: "Factura recibida",
          liquidated: "Liquidado",
          inconsistency: "Inconsistencia",
          rejected: "Rechazado",
        }[s] || s
      );
    },

    /**
     * @method loadInconsistencyCount
     * @description Carga la cantidad de lotes con inconsistencias para el badge
     */
    async loadInconsistencyCount() {
      try {
        const data = await liquidationBatchService.getInconsistencies({});
        const items = data.docs || data;
        this.inconsistencyCount = items.length;
      } catch (e) {
        this.inconsistencyCount = 0;
      }
    },
  },
};
</script>

<style scoped>
.liq {
  font-family: "Plus Jakarta Sans", sans-serif;
}

.liq__subtabs {
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e2e8f0;
  padding-bottom: 0;
}

.liq__subtab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  padding: 7px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #6b7294;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s, border-color 0.15s;
}

.liq__subtab.is-active {
  color: #002d5e;
  border-bottom-color: #002d5e;
}

.liq__badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #e24b4a;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  padding: 0 5px;
  margin-left: 6px;
}

.liq__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.liq__card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.liq__trx {
  font-weight: 700;
  color: #002d5e;
  font-size: 12px;
  font-family: monospace;
}

.liq__date {
  font-size: 12px;
  color: #6b7294;
}

.liq__name {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}

.liq__sub {
  font-size: 11px;
  color: #05e5ba;
  font-weight: 600;
}

.liq__code {
  font-weight: 700;
  color: #05e5ba;
  font-size: 11px;
  margin-right: 4px;
}

.liq__pname {
  color: #1a1f36;
  font-size: 13px;
}

.liq__prov {
  font-size: 13px;
  color: #1a1f36;
}

.liq__amount {
  font-size: 13px;
  font-weight: 600;
  color: #1a1f36;
}

.liq__amount--mismatch {
  color: #c0392b;
}

.liq__badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.liq__badge--pending {
  background: #f0f2f7;
  color: #6b7294;
}

.liq__badge--invoice {
  background: #eeedfe;
  color: #26215c;
}

.liq__badge--liquidated {
  background: #eaf3de;
  color: #3b6d11;
}

.liq__badge--inconsistency {
  background: #fcebeb;
  color: #a32d2d;
}

.liq__badge--rejected {
  background: #f5f5f5;
  color: #909399;
}

.liq__badge--validation {
  background: #e1f5ee;
  color: #085041;
}

.liq__badge--auth {
  background: #eeedfe;
  color: #26215c;
}

.liq__badge--audited {
  background: #faeeda;
  color: #412402;
}

.liq__pagination {
  padding: 12px 16px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f0f2f7;
}

/* Resumen */
.liq__summary-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.liq__summary-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.liq__summary-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 28px 24px;
  text-align: center;
}

.liq__summary-card--green {
  border-color: #c3e6cb;
  background: #f4fbf0;
}

.liq__summary-card--orange {
  border-color: #f5c6a0;
  background: #fef8f0;
}

.liq__summary-card--gray {
  border-color: #e2e8f0;
  background: #f8f9fb;
}

.liq__summary-value {
  font-size: 40px;
  font-weight: 800;
  color: #002d5e;
  line-height: 1;
}

.liq__summary-label {
  font-size: 13px;
  color: #6b7294;
  margin-top: 8px;
  font-weight: 500;
}

/* Dialog */
.liq__dialog {
  font-size: 13px;
}

.liq__dialog-section {
  font-weight: 700;
  color: #002d5e;
  font-size: 13px;
  margin-bottom: 10px;
}

.liq__dialog-row {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.liq__dialog-label {
  font-weight: 600;
  color: #6b7294;
  min-width: 110px;
}

.liq__field-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7294;
  margin-bottom: 4px;
}

.liq__upload-tip {
  font-size: 11px;
  color: #94a3b8;
  margin-left: 8px;
}
</style>
